import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { LogIn, LogOut, Clock, Calendar, User, Menu, Gift, X, AlertTriangle, TrendingUp, TrendingDown, Timer } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import api from '../api/axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Auto-checkout times
const MORNING_SESSION_CHECKOUT = { hour: 13, minute: 30 }; // 1:30 PM
const EVENING_SESSION_CHECKOUT = { hour: 19, minute: 0 };  // 7:00 PM
const AUTO_CHECKOUT_COUNTDOWN_SECONDS = 300; // 5 minutes countdown

// Request browser notification permission
const requestNotificationPermission = async () => {
  if ('Notification' in window) {
    if (Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }
};

// Show browser notification
const showBrowserNotification = (title, body, tag) => {
  try {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body: body,
        icon: '/favicon.ico',
        tag: tag, // Prevents duplicate notifications
        requireInteraction: true, // Keeps notification visible until user interacts
        vibrate: [200, 100, 200] // Vibration pattern for mobile
      });

      // Focus the window when notification is clicked
      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      return notification;
    }
  } catch {
    // Notification API can be flaky in some in-app/embedded browsers.
  }
  return null;
};

const EmployeeDashboard = ({ user, onLogout }) => {
  const [todayStatus, setTodayStatus] = useState(null);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBirthdayWish, setShowBirthdayWish] = useState(false);
  
  // Overtime state
  const [overtimeData, setOvertimeData] = useState(null);
  const [overtimeLoading, setOvertimeLoading] = useState(false);
  
  // Birthday Leave state
  const [birthdayEligibility, setBirthdayEligibility] = useState(null);
  const [showBirthdayLeaveModal, setShowBirthdayLeaveModal] = useState(false);
  const [birthdayLeaveDate, setBirthdayLeaveDate] = useState('');
  const [birthdayLeaveLoading, setBirthdayLeaveLoading] = useState(false);
  
  // Auto-checkout state
  const [showAutoCheckoutModal, setShowAutoCheckoutModal] = useState(false);
  const [autoCheckoutCountdown, setAutoCheckoutCountdown] = useState(AUTO_CHECKOUT_COUNTDOWN_SECONDS);
  const [nextCheckTime, setNextCheckTime] = useState(null);
  const [currentSessionType, setCurrentSessionType] = useState(''); // 'morning' or 'evening'
  const autoCheckoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Check if today is user's birthday
  const isBirthday = () => {
    if (!user?.birthday) return false;
    try {
      const today = new Date();
      const birthday = new Date(user.birthday);
      if (Number.isNaN(birthday.getTime())) return false;
      return today.getMonth() === birthday.getMonth() && today.getDate() === birthday.getDate();
    } catch {
      return false;
    }
  };

  // Auto-checkout function
  const performAutoCheckout = useCallback(async () => {
    const checkoutTimeLabel = currentSessionType === 'morning' ? '1:30 PM' : '7:00 PM';

    // Clear stored checkout time
    localStorage.removeItem('autoCheckoutTime');

    try {
      await api.post('/attendance/checkout', { auto_checkout: true });
      toast.info(`You have been automatically checked out at ${checkoutTimeLabel}`);
      showBrowserNotification('Auto Checkout', `You have been automatically checked out at ${checkoutTimeLabel}`, 'auto-checkout');
      setShowAutoCheckoutModal(false);
      setCurrentSessionType('');
      fetchTodayStatus();
      fetchRecentAttendance();
    } catch (error) {
      toast.error('Auto-checkout failed. Please check out manually.');
      showBrowserNotification('Auto Checkout Failed', 'Please check out manually.', 'auto-checkout-failed');
      setShowAutoCheckoutModal(false);
    }
  }, [currentSessionType]);

  // Handle "Still Working" button click
  const handleStillWorking = useCallback(() => {
    setShowAutoCheckoutModal(false);
    setAutoCheckoutCountdown(AUTO_CHECKOUT_COUNTDOWN_SECONDS);
    
    // Clear stored checkout time
    localStorage.removeItem('autoCheckoutTime');
    
    // Clear any existing timers
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    
    // Schedule next check in 30 minutes for morning, 1 hour for evening
    const nextCheck = new Date();
    if (currentSessionType === 'morning') {
      nextCheck.setMinutes(nextCheck.getMinutes() + 30); // 30 min for morning (lunch might extend)
    } else {
      nextCheck.setHours(nextCheck.getHours() + 1); // 1 hour for evening
    }
    setNextCheckTime(nextCheck.getTime());
    
    const nextCheckLabel = currentSessionType === 'morning' ? '30 minutes' : '1 hour';
    toast.success(`Great! We'll check again in ${nextCheckLabel}.`);
  }, [currentSessionType]);

  // Start countdown when modal is shown - with background tab support
  useEffect(() => {
    if (showAutoCheckoutModal) {
      // Store the auto-checkout deadline in localStorage
      const checkoutDeadline = Date.now() + (AUTO_CHECKOUT_COUNTDOWN_SECONDS * 1000);
      localStorage.setItem('autoCheckoutTime', checkoutDeadline.toString());
      
      // Show browser notification
      showBrowserNotification(
        '⚠️ Still in Office?',
        `Auto-checkout in 5 minutes. Click to respond.`,
        'still-in-office'
      );
      
      setAutoCheckoutCountdown(AUTO_CHECKOUT_COUNTDOWN_SECONDS);
      
      countdownIntervalRef.current = setInterval(() => {
        // Check against stored deadline (handles background tab slowdown)
        const deadline = parseInt(localStorage.getItem('autoCheckoutTime') || '0');
        const remainingMs = deadline - Date.now();
        const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
        
        if (remainingSec <= 0) {
          clearInterval(countdownIntervalRef.current);
          performAutoCheckout();
          setAutoCheckoutCountdown(0);
        } else {
          setAutoCheckoutCountdown(remainingSec);
        }
      }, 1000);
      
      return () => {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
      };
    }
  }, [showAutoCheckoutModal, performAutoCheckout]);

  // Handle visibility change - check for pending auto-checkout when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const deadline = parseInt(localStorage.getItem('autoCheckoutTime') || '0');
        if (deadline > 0) {
          const remainingMs = deadline - Date.now();
          if (remainingMs <= 0) {
            // Time has passed while tab was in background - auto checkout now
            localStorage.removeItem('autoCheckoutTime');
            performAutoCheckout();
          } else {
            // Update countdown
            setAutoCheckoutCountdown(Math.ceil(remainingMs / 1000));
          }
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [performAutoCheckout]);

  // Request notification permission on mount - automatically for all employees
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Check for auto-checkout time (1:30 PM for morning session, 7:00 PM for evening session)
  useEffect(() => {
    const checkAutoCheckoutTime = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      
      // Only proceed if employee is checked in
      if (todayStatus?.status !== 'checked_in') {
        return;
      }
      
      // If there's a next check time set (user clicked "Still Working"), respect it
      if (nextCheckTime && now.getTime() < nextCheckTime) {
        return;
      }
      
      // Don't show modal if already showing
      if (showAutoCheckoutModal) {
        return;
      }
      
      // Count completed sessions today
      const sessions = Array.isArray(todayStatus?.sessions) ? todayStatus.sessions : [];
      const completedSessions = sessions.filter(s => s.check_out).length;
      
      // Morning session checkout (1:30 PM = 13:30)
      const morningCheckoutTime = MORNING_SESSION_CHECKOUT.hour * 60 + MORNING_SESSION_CHECKOUT.minute;
      // Evening session checkout (7:00 PM = 19:00)
      const eveningCheckoutTime = EVENING_SESSION_CHECKOUT.hour * 60 + EVENING_SESSION_CHECKOUT.minute;
      
      // First session (morning): Auto-checkout at 1:30 PM
      if (completedSessions === 0 && currentTimeInMinutes >= morningCheckoutTime && currentTimeInMinutes < eveningCheckoutTime) {
        setCurrentSessionType('morning');
        setShowAutoCheckoutModal(true);
        return;
      }
      
      // Second session (evening) or any session after 7 PM: Auto-checkout at 7:00 PM
      if (currentTimeInMinutes >= eveningCheckoutTime) {
        setCurrentSessionType('evening');
        setShowAutoCheckoutModal(true);
        return;
      }
    };
    
    // Check immediately
    checkAutoCheckoutTime();
    
    // Check every minute
    const intervalId = setInterval(checkAutoCheckoutTime, 60000);
    
    return () => clearInterval(intervalId);
  }, [todayStatus, nextCheckTime, showAutoCheckoutModal]);

  // Format countdown for display
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    fetchTodayStatus();
    fetchRecentAttendance();
    fetchLeaveBalance();
    fetchUpcomingHolidays();
    fetchOvertimeStatus();
    fetchBirthdayEligibility();
    
    // Show birthday wish if it's user's birthday
    if (isBirthday()) {
      setShowBirthdayWish(true);
    }
  }, []);

  // Shared check-in/check-out flow
  const attemptCheckInOut = async (action) => {
    setLoading(true);

    const label = action === 'in' ? 'Check-in' : 'Check-out';

    try {
      await api.post(action === 'in' ? '/attendance/checkin' : '/attendance/checkout', {});
      toast.success(`${label} successful!`);
      fetchTodayStatus();
      fetchRecentAttendance();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || `${label} failed`;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check-in
  const handleCheckIn = () => attemptCheckInOut('in');

  // Check-out
  const handleCheckOut = () => attemptCheckInOut('out');

  const fetchTodayStatus = async () => {
    try {
      const response = await api.get('/attendance/today-status');
      setTodayStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch today status');
    }
  };

  const fetchRecentAttendance = async () => {
    try {
      const response = await api.get('/attendance/my-records');
      setRecentAttendance(response.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch attendance');
    }
  };

  const fetchLeaveBalance = async () => {
    try {
      const response = await api.get('/leaves/balance');
      setLeaveBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch leave balance');
    }
  };

  const fetchUpcomingHolidays = async () => {
    try {
      const response = await api.get('/holidays/upcoming');
      setUpcomingHolidays(response.data);
    } catch (error) {
      console.error('Failed to fetch holidays');
    }
  };

  const fetchOvertimeStatus = async () => {
    setOvertimeLoading(true);
    try {
      const response = await api.get('/employee/overtime-status');
      setOvertimeData(response.data);
    } catch (error) {
      console.error('Failed to fetch overtime status');
    } finally {
      setOvertimeLoading(false);
    }
  };

  const fetchBirthdayEligibility = async () => {
    try {
      const response = await api.get('/leaves/birthday-eligibility');
      setBirthdayEligibility(response.data);
    } catch (error) {
      console.error('Failed to fetch birthday eligibility');
    }
  };

  const applyBirthdayLeave = async () => {
    if (!birthdayLeaveDate) {
      toast.error('Please select a date for your birthday leave');
      return;
    }
    
    setBirthdayLeaveLoading(true);
    try {
      await api.post(`/leaves/birthday-leave?leave_date=${birthdayLeaveDate}`);
      toast.success('Birthday leave applied successfully!');
      setShowBirthdayLeaveModal(false);
      setBirthdayLeaveDate('');
      fetchBirthdayEligibility(); // Refresh eligibility
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply birthday leave');
    } finally {
      setBirthdayLeaveLoading(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      return format(new Date(isoString), 'hh:mm a');
    } catch {
      return 'N/A';
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const calculateSessionDuration = (checkIn, checkOut) => {
    if (!checkOut) return null;
    try {
      const start = new Date(checkIn);
      const end = new Date(checkOut);
      const diffMs = end - start;
      const diffMins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMins / 60);
      const minutes = diffMins % 60;
      return { hours, minutes, totalMinutes: diffMins };
    } catch {
      return null;
    }
  };

  const calculateTotalWorkingHours = () => {
    if (!Array.isArray(sessions) || sessions.length === 0) return { hours: 0, minutes: 0 };
    
    let totalMinutes = 0;
    sessions.forEach(session => {
      const duration = calculateSessionDuration(session.check_in, session.check_out);
      if (duration) {
        totalMinutes += duration.totalMinutes;
      }
    });

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { hours, minutes };
  };

  const formatDuration = (hours, minutes) => {
    if (hours === 0 && minutes === 0) return '0m';
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  const canCheckIn = !todayStatus || todayStatus.status === 'not_checked_in' || todayStatus.status === 'checked_out';
  const canCheckOut = todayStatus && todayStatus.status === 'checked_in';
  const sessions = Array.isArray(todayStatus?.sessions) ? todayStatus.sessions : [];
  const totalWorkingHours = calculateTotalWorkingHours();
  const completedSessions = sessions.filter(s => s.check_out).length;

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} onLogout={onLogout} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mb-8">
            <h1 className="text-4xl font-bold font-heading tracking-tight mb-2">
              {getGreeting()}, {user.name}!
            </h1>
            <p className="text-lg text-muted-foreground">
              {format(new Date(), 'EEEE, MMMM d, yyyy')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
            <div className="md:col-span-8">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <h2 className="text-xl font-semibold font-heading tracking-tight mb-6">
                  Attendance
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <Button
                    data-testid="check-in-button"
                    onClick={handleCheckIn}
                    disabled={!canCheckIn || loading}
                    className="h-24 text-lg"
                    variant={canCheckIn ? 'default' : 'secondary'}
                  >
                    <LogIn className="mr-2 h-5 w-5" />
                    Check In
                  </Button>

                  <Button
                    data-testid="check-out-button"
                    onClick={handleCheckOut}
                    disabled={!canCheckOut || loading}
                    className="h-24 text-lg"
                    variant={canCheckOut ? 'default' : 'secondary'}
                  >
                    <LogOut className="mr-2 h-5 w-5" />
                    Check Out
                  </Button>
                </div>

                {sessions.length > 0 && (
                  <div className="space-y-4">
                    <div className="bg-muted rounded-md p-4">
                      <h3 className="text-sm font-semibold mb-3">Today's Sessions</h3>
                      <div className="space-y-2">
                        {sessions.map((session, index) => {
                          const duration = calculateSessionDuration(session.check_in, session.check_out);
                          return (
                            <div key={index} className="flex items-center justify-between bg-white rounded p-3 border border-slate-200">
                              <div className="flex items-center gap-4">
                                <div className="text-center">
                                  <p className="text-xs text-muted-foreground">Session {index + 1}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Check In</p>
                                  <p className="text-sm font-semibold">{formatTime(session.check_in)}</p>
                                </div>
                                <span className="text-muted-foreground">→</span>
                                <div>
                                  <p className="text-xs text-muted-foreground">Check Out</p>
                                  <p className="text-sm font-semibold">
                                    {session.check_out ? formatTime(session.check_out) : 'Active'}
                                  </p>
                                </div>
                                {duration && (
                                  <>
                                    <span className="text-muted-foreground">•</span>
                                    <div>
                                      <p className="text-xs text-muted-foreground">Duration</p>
                                      <p className="text-sm font-semibold text-accent">
                                        {formatDuration(duration.hours, duration.minutes)}
                                      </p>
                                    </div>
                                  </>
                                )}
                              </div>
                              {!session.check_out && (
                                <span className="inline-flex items-center rounded-full bg-success/10 px-2.5 py-0.5 text-xs font-semibold text-success">
                                  Active
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="bg-accent/5 border-2 border-accent/20 rounded-md p-4">
                      <h3 className="text-sm font-semibold mb-3 text-accent">Today's Summary</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Total Sessions</p>
                          <p className="text-2xl font-bold text-accent" data-testid="total-sessions">
                            {sessions.length}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Completed</p>
                          <p className="text-2xl font-bold text-success" data-testid="completed-sessions">
                            {completedSessions}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Total Working Hours</p>
                          <p className="text-2xl font-bold text-primary" data-testid="total-working-hours">
                            {formatDuration(totalWorkingHours.hours, totalWorkingHours.minutes)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="md:col-span-4 space-y-6">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <h3 className="text-lg font-semibold font-heading tracking-tight mb-4">
                  Upcoming Holidays
                </h3>
                <div className="space-y-3" data-testid="upcoming-holidays-list">
                  {upcomingHolidays.length > 0 ? (
                    upcomingHolidays.map((holiday) => (
                      <div
                        key={holiday.holiday_id}
                        className="border-l-4 border-accent pl-3 py-2"
                      >
                        <p className="font-semibold text-sm">{holiday.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(holiday.date)}
                        </p>
                        {holiday.description && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {holiday.description}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No upcoming holidays
                    </p>
                  )}
                </div>
              </div>

              {/* Overtime/Undertime Status */}
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold font-heading tracking-tight flex items-center gap-2">
                    <Timer className="h-5 w-5 text-accent" />
                    Monthly Hours & Leave Status
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchOvertimeStatus}
                    disabled={overtimeLoading}
                  >
                    {overtimeLoading ? 'Loading...' : 'Refresh'}
                  </Button>
                </div>

                {overtimeData ? (
                  <div className="space-y-3">
                    <div className="text-center pb-2 border-b">
                      <p className="text-sm font-medium text-slate-700">{overtimeData.current_month} {overtimeData.year}</p>
                    </div>

                    {/* Hours Summary */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-blue-600">Expected Hours</p>
                        <p className="text-lg font-bold text-blue-800">{overtimeData.expected_hours}h</p>
                      </div>
                      <div className="bg-indigo-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-indigo-600">Hours Worked</p>
                        <p className="text-lg font-bold text-indigo-800">{overtimeData.total_hours_worked}h</p>
                      </div>
                    </div>

                    {/* Deficit / Extra */}
                    <div className={`rounded-lg p-3 text-center ${overtimeData.hours_difference >= 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center justify-center gap-2">
                        {overtimeData.hours_difference >= 0 ? (
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-600" />
                        )}
                        <p className={`text-xs font-medium ${overtimeData.hours_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {overtimeData.hours_difference >= 0 ? 'Extra Hours' : 'Deficit Hours'}
                        </p>
                      </div>
                      <p className={`text-xl font-bold ${overtimeData.hours_difference >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {overtimeData.hours_difference >= 0 ? '+' : ''}{overtimeData.hours_difference}h
                      </p>
                    </div>

                    {/* Carry Over */}
                    {overtimeData.carry_over_hours !== 0 && (
                      <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
                        <div className="flex justify-between items-center">
                          <p className="text-xs text-amber-700">Carry Over (Previous Months)</p>
                          <p className="text-sm font-bold text-amber-800">
                            {overtimeData.carry_over_hours >= 0 ? '+' : ''}{overtimeData.carry_over_hours}h
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Leave Impact */}
                    {overtimeData.potential_leave_adjustment !== 0 && (
                      <div className={`rounded-lg p-3 border ${overtimeData.potential_leave_adjustment > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                        <p className="text-xs text-center mb-1 text-slate-600">Leave Impact (if processed)</p>
                        <p className={`text-lg font-bold text-center ${overtimeData.potential_leave_adjustment > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {overtimeData.potential_leave_adjustment > 0 ? '+' : ''}{overtimeData.potential_leave_adjustment} paid leave{Math.abs(overtimeData.potential_leave_adjustment) !== 1 ? 's' : ''}
                          {overtimeData.potential_leave_adjustment > 0 ? ' earned' : ' deducted'}
                        </p>
                      </div>
                    )}

                    {/* Paid Leave Balance */}
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <p className="text-xs text-slate-500 text-center mb-2">Paid Leave Balance</p>
                      <div className="flex justify-center items-baseline gap-1">
                        <p className={`text-2xl font-bold ${overtimeData.current_paid_leaves >= 0 ? 'text-slate-800' : 'text-red-700'}`}>
                          {overtimeData.current_paid_leaves}
                        </p>
                        <p className="text-xs text-slate-500">leaves</p>
                      </div>
                      {overtimeData.current_paid_leaves < 0 && (
                        <p className="text-xs text-red-600 text-center mt-1">
                          Deficit: {Math.abs(overtimeData.current_paid_leaves)} leave(s) to recover
                        </p>
                      )}
                    </div>

                    <div className="text-xs text-muted-foreground text-center pt-2 border-t">
                      <p>Rule: Every 6 extra hours = +1 leave | Every 6 hours short = -1 leave</p>
                      {overtimeData.last_processed_month && (
                        <p className="mt-1 text-slate-400">Last processed: {overtimeData.last_processed_month}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <Timer className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Loading overtime status...</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Birthday Leave Banner - Show when eligible */}
          {birthdayEligibility?.eligible && (
            <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center">
                    <Gift className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-purple-800">Birthday Leave Available! 🎂</p>
                    <p className="text-sm text-purple-600">
                      You worked full day on your birthday. Claim your half-day leave by {birthdayEligibility.window_end}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowBirthdayLeaveModal(true)}
                  className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                >
                  Apply Now
                </Button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <h2 className="text-xl font-semibold font-heading tracking-tight mb-4">
              Recent Attendance History
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="recent-attendance-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold">Sessions</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold">Total Working Hours</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAttendance.map((record) => {
                    const recordSessions = Array.isArray(record.sessions) ? record.sessions : [];
                    const completedSessions = recordSessions.filter(s => s.check_out).length;
                    const hasActiveSessions = recordSessions.some(s => !s.check_out);
                    
                    // Calculate total hours for this day
                    let totalMinutes = 0;
                    recordSessions.forEach(session => {
                      const duration = calculateSessionDuration(session.check_in, session.check_out);
                      if (duration) {
                        totalMinutes += duration.totalMinutes;
                      }
                    });
                    const hours = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;
                    
                    return (
                      <tr key={record.attendance_id} className="border-b border-slate-100">
                        <td className="py-3 px-4 text-sm font-medium">{record.date}</td>
                        <td className="py-3 px-4 text-sm">
                          <span className="text-accent font-semibold">{recordSessions.length}</span> session{recordSessions.length !== 1 ? 's' : ''}
                          {completedSessions < recordSessions.length && (
                            <span className="text-xs text-muted-foreground ml-1">
                              ({completedSessions} completed)
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <span className="font-semibold text-primary">
                            {formatDuration(hours, minutes)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                              hasActiveSessions
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-emerald-100 text-emerald-800'
                            }`}
                          >
                            {hasActiveSessions ? 'In Progress' : 'Completed'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {recentAttendance.length === 0 && (
                    <tr>
                      <td colSpan="4" className="py-8 px-4 text-center text-muted-foreground">
                        No attendance records yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Birthday Wish Modal */}
      {showBirthdayWish && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, type: "spring" }}
            className="bg-gradient-to-br from-amber-50 to-orange-100 rounded-2xl p-8 w-full max-w-md mx-4 text-center relative shadow-2xl border-2 border-amber-200"
          >
            <button
              onClick={() => setShowBirthdayWish(false)}
              className="absolute top-4 right-4 text-amber-600 hover:text-amber-800"
            >
              <X className="h-6 w-6" />
            </button>
            
            <div className="mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Gift className="h-10 w-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-amber-800 mb-2">
                Happy Birthday! 🎂
              </h2>
            </div>
            
            <div className="space-y-4">
              <p className="text-lg text-amber-900 font-medium">
                Dear {user?.name?.split(' ')[0] || 'Team Member'},
              </p>
              <p className="text-amber-800 leading-relaxed">
                <strong>S.N. Randar & Associates</strong> wishes you a very <strong>Happy Birthday!</strong>
              </p>
              <p className="text-amber-700">
                Enjoy your special day! 🎉
              </p>
              <p className="text-sm text-amber-600 mt-4 bg-amber-100 p-3 rounded-lg">
                🎁 You have a <strong>half-day paid leave</strong> today as a birthday gift!
              </p>
            </div>
            
            <Button
              onClick={() => setShowBirthdayWish(false)}
              className="mt-6 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8"
            >
              Thank You! 🎈
            </Button>
          </motion.div>
        </div>
      )}

      {/* Auto-Checkout Modal */}
      {showAutoCheckoutModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, type: "spring" }}
            className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 text-center relative shadow-2xl border-2 border-orange-300"
          >
            <div className="mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg animate-pulse">
                <AlertTriangle className="h-10 w-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Still in Office?
              </h2>
              <p className="text-gray-600">
                {currentSessionType === 'morning' 
                  ? "It's past 1:30 PM (Lunch break time). Are you still working?"
                  : "It's past 7:00 PM. Are you still working?"}
              </p>
            </div>
            
            <div className="mb-6">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <p className="text-sm text-orange-800 mb-2">
                  Auto-checkout in
                </p>
                <div className="text-4xl font-bold text-orange-600 font-mono">
                  {formatCountdown(autoCheckoutCountdown)}
                </div>
                <p className="text-xs text-orange-600 mt-2">
                  Click "Still Working" to continue
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={handleStillWorking}
                className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-lg font-semibold"
                data-testid="still-working-btn"
              >
                ✋ Still Working
              </Button>
              
              <Button
                onClick={() => {
                  setShowAutoCheckoutModal(false);
                  performAutoCheckout();
                }}
                variant="outline"
                className="w-full h-10 text-gray-600 border-gray-300"
                data-testid="checkout-now-btn"
              >
                Check Out Now
              </Button>
            </div>
            
            <p className="text-xs text-gray-500 mt-4">
              {currentSessionType === 'morning'
                ? "If you click \"Still Working\", we'll ask again in 30 minutes."
                : "If you click \"Still Working\", we'll ask again in 1 hour."}
            </p>
          </motion.div>
        </div>
      )}

      {/* Birthday Leave Application Modal */}
      {showBirthdayLeaveModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Gift className="h-6 w-6 text-pink-500" />
                Birthday Leave
              </h3>
              <button
                onClick={() => setShowBirthdayLeaveModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Congratulations! You're eligible for a half-day birthday leave because you worked full day on your birthday.
              </p>
              
              <div className="bg-pink-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-pink-700">
                  <strong>Note:</strong> This half-day leave is a special benefit and does NOT deduct from your paid leave balance.
                </p>
              </div>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select date for your birthday leave
              </label>
              <input
                type="date"
                value={birthdayLeaveDate}
                onChange={(e) => setBirthdayLeaveDate(e.target.value)}
                min={birthdayEligibility?.window_start || ''}
                max={birthdayEligibility?.window_end || ''}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              />
              {birthdayEligibility?.window_end && (
                <p className="text-xs text-gray-500 mt-1">
                  Must be taken by {birthdayEligibility.window_end}
                </p>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => setShowBirthdayLeaveModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={applyBirthdayLeave}
                disabled={birthdayLeaveLoading || !birthdayLeaveDate}
                className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
              >
                {birthdayLeaveLoading ? 'Applying...' : 'Apply Leave'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard;